import React from 'react';
import { Loader2, FolderPlus, Search, Clock, Database } from 'lucide-react';

interface LoadingStateProps {
  message: string;
  description?: string;
  phase?: 'workspace' | 'search' | 'processing' | 'database';
  className?: string;
}

/**
 * Enhanced loading state component with Color Kit compliance
 * Shows different icons and animations based on the current phase
 */
export const EnhancedLoadingState: React.FC<LoadingStateProps> = ({
  message,
  description,
  phase = 'processing',
  className = ''
}) => {
  const getPhaseIcon = () => {
    const iconClasses = "w-5 h-5 text-primary";
    
    switch (phase) {
      case 'workspace':
        return <FolderPlus className={`${iconClasses} animate-pulse`} />;
      case 'search':
        return <Search className={`${iconClasses} animate-bounce`} />;
      case 'database':
        return <Database className={`${iconClasses} animate-pulse`} />;
      default:
        return <Loader2 className={`${iconClasses} animate-spin`} />;
    }
  };

  const getProgressIndicator = () => {
    return (
      <div className="flex items-center gap-1 mt-2">
        <div className="w-2 h-2 bg-primary rounded-full animate-pulse"></div>
        <div className="w-2 h-2 bg-primary/70 rounded-full animate-pulse delay-100"></div>
        <div className="w-2 h-2 bg-primary/50 rounded-full animate-pulse delay-200"></div>
      </div>
    );
  };

  return (
    <div className={`
      flex flex-col items-center justify-center py-8 px-6
      bg-card rounded-lg border border-border
      shadow-subtle-lift
      ${className}
    `}>
      {/* Icon with Color Kit primary styling */}
      <div className="flex items-center justify-center w-12 h-12 mb-4 bg-accent rounded-full">
        {getPhaseIcon()}
      </div>
      
      {/* Main message with Color Kit typography */}
      <div className="text-center">
        <h3 className="text-lg font-semibold text-foreground mb-2">
          {message}
        </h3>
        
        {description && (
          <p className="text-sm text-muted-foreground max-w-md mx-auto">
            {description}
          </p>
        )}
        
        {/* Progress indicator with Color Kit primary colors */}
        {getProgressIndicator()}
      </div>
    </div>
  );
};

/**
 * Specialized loading component for workspace creation
 */
export const WorkspaceCreationLoading: React.FC<{ workspaceName: string }> = ({ workspaceName }) => (
  <EnhancedLoadingState
    phase="workspace"
    message="Creating workspace..."
    description={`Setting up "${workspaceName}" for your keyword research.`}
    className="bg-accent/30 border-accent"
  />
);

/**
 * Specialized loading component for search operations
 */
export const SearchProgressLoading: React.FC<{ keyword: string; workspaceName?: string }> = ({ 
  keyword, 
  workspaceName 
}) => (
  <EnhancedLoadingState
    phase="search"
    message="Starting keyword research..."
    description={
      workspaceName 
        ? `Processing "${keyword}" in workspace "${workspaceName}".`
        : `Processing "${keyword}". This may take a moment.`
    }
    className="bg-primary/5 border-primary/20"
  />
);

/**
 * Specialized loading component for data processing
 */
export const DataProcessingLoading: React.FC<{ message?: string }> = ({ 
  message = "Processing data..." 
}) => (
  <EnhancedLoadingState
    phase="processing"
    message={message}
    description="Analyzing and organizing your keyword data."
    className="bg-accent/20 border-accent/50"
  />
);

/**
 * Inline loading indicator for search form button
 */
export const InlineLoadingIndicator: React.FC<{ 
  message: string;
  phase?: 'workspace' | 'search' | 'processing';
}> = ({ message, phase = 'processing' }) => {
  const getIcon = () => {
    const iconClasses = "w-4 h-4 text-primary";
    
    switch (phase) {
      case 'workspace':
        return <FolderPlus className={`${iconClasses} animate-pulse`} />;
      case 'search':
        return <Search className={`${iconClasses} animate-bounce`} />;
      default:
        return <Loader2 className={`${iconClasses} animate-spin`} />;
    }
  };

  return (
    <div className="
      flex items-center gap-2 px-3 py-2 
      bg-muted rounded-md border border-border
      text-sm text-muted-foreground
    ">
      {getIcon()}
      <span>{message}</span>
    </div>
  );
};

/**
 * Full screen loading overlay for critical operations
 */
export const LoadingOverlay: React.FC<{
  message: string;
  description?: string;
  phase?: 'workspace' | 'search' | 'processing';
}> = ({ message, description, phase = 'processing' }) => (
  <div className="
    fixed inset-0 z-50 
    bg-background/80 backdrop-blur-sm
    flex items-center justify-center
  ">
    <div className="
      bg-card rounded-xl border border-border p-8
      shadow-2xl max-w-md w-full mx-4
    ">
      <EnhancedLoadingState
        message={message}
        description={description}
        phase={phase}
        className="bg-transparent border-none shadow-none p-0"
      />
    </div>
  </div>
);
